import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Coerce, KalDialogConfig, KalDialogService, KalSnackbarService } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import gql from 'graphql-tag';
import cloneDeep from 'lodash-es/cloneDeep';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, delay, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { IGNORE_ALL_GRAPHQL_ERRORS } from 'src/app/_core/graphql.module';
import { ActivityPrintMailService } from 'src/app/_core/services/activity-print-mail.service';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { LayoutService } from 'src/app/_core/services/layout.service';
import { ListingComponent } from 'src/app/_shared/components/listing/listing.component';
import {
    SharedInvoicesFiltersComponent
} from 'src/app/_shared/components/shared-invoices-filters/shared-invoices-filters.component';
import {
    SharedInvoicesListDatasource
} from 'src/app/_shared/components/shared-invoices-list/shared-invoices-list.datasource';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';
import {
    SharedMailDialogComponent
} from 'src/app/_shared/dialogs/mail/shared-mail-dialog/shared-mail-dialog.component';
import {
    SharedAdvancedSettlementDialogComponent,
    SharedAdvancedSettlementDialogData
} from 'src/app/_shared/dialogs/settlements/shared-advanced-settlement-dialog/shared-advanced-settlement-dialog.component';
import {
    SharedDeleteCreditDialogComponent,
    SharedDeleteCreditDialogData
} from 'src/app/_shared/dialogs/settlements/shared-delete-credit-dialog/shared-delete-credit-dialog.component';
import {
    SharedGenerateCreditDialogComponent,
    SharedGenerateCreditDialogData
} from 'src/app/_shared/dialogs/settlements/shared-generate-credit-dialog/shared-generate-credit-dialog.component';
import {
    SharedChequeLetterDialogComponent,
    SharedChequeLetterDialogData
} from 'src/app/_shared/dialogs/shared-cheque-letter-dialog/shared-cheque-letter-dialog.component';
import {
    SharedInvoicesOrdersListDialogComponent,
} from 'src/app/_shared/dialogs/shared-invoices-orders-list-dialog/shared-invoices-orders-list-dialog.component';
import {
    SharedPrintOptionsDialogComponent,
    SharedPrintOptionsDialogData
} from 'src/app/_shared/dialogs/shared-print-options/shared-print-options-dialog.component';
import {
    ViewerDocumentDialogComponent,
    ViewerDocumentDialogData
} from 'src/app/_shared/dialogs/viewer-document-dialog/viewer-document-dialog.component';
import { ColumnFilterType } from 'src/app/_shared/modules/kal-table/kal-table-header/kal-table-header.component';
import {
    KalTableComponent,
    KalTableHeader,
    KalTableSortDirection
} from 'src/app/_shared/modules/kal-table/kal-table.module';
import { UserState } from 'src/app/_user/store/user/user.state';
import {
    ActivityInvoice
} from 'src/app/board/activity/activity-shared/activity-beneficiaries-invoices/classes/activity-invoice';
import { isFree } from 'src/app/board/activity/activity-shared/activity-free-entries/activity-free-entries.constant';
import { TypeActivite, TypeReglement } from 'src/app/board/activity/activity.constants';
import { ActivitySalesState } from 'src/app/board/activity/stores/activity-sales/activity-sales.state';
import { graphqlErrors } from 'src/app/utils/errors.utils';
import { exportAmount, exportDate } from 'src/app/utils/kal-table-csv-export.utils';
import { constants } from 'src/environments/constants';
import {
    FactureHistoriqueModel,
    MutationGetListeCommandeHistoriqueArgs,
    MutationIsLettreChequeImprimablePourFactureArgs,
    MutationIsSupprimableFactureArgs,
    MutationSupprimerFactureFournisseurArgs,
    MutationSupprimerFactureReservationArgs,
    MutationVerifMontantRestantAvoirSurFactureArgs
} from 'src/graphql/generated-types';

@Component({
    selector: 'app-shared-invoices-list',
    templateUrl: './shared-invoices-list.component.html',
    styleUrls: ['./shared-invoices-list.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedInvoicesListComponent<T extends FactureHistoriqueModel> extends ActivityInvoice implements OnInit {

    static readonly listingKey: string = 'activity-sales-invoices-listing';

    static defaultSort = {
        code: 'DateFacture',
        direction: KalTableSortDirection.DESC
    };

    activityId$: Observable<number> = inject(Store).select(ActivitySalesState.currentActivityId);

    dataSource: SharedInvoicesListDatasource;

    columns: KalTableHeader<T>[] = [
        {
            code: 'StatutFacture',
            label: 'Statut',
            sortable: true,
            columnFilterType: ColumnFilterType.List,
            columnFilterList: [
                {code: constants.facture.statut.SOLDE, label: this.getStatusClass(constants.facture.statut.SOLDE).label, isDisplayed: true},
                {code: constants.facture.statut.NON_SOLDE, label: this.getStatusClass(constants.facture.statut.NON_SOLDE).label, isDisplayed: true},
                {code: constants.facture.statut.PARTIELLEMENT_SOLDE, label: this.getStatusClass(constants.facture.statut.PARTIELLEMENT_SOLDE).label, isDisplayed: true},
            ]
        },
        {code: 'DateFacture', label: 'Date', sortable: true, columnFilterType: ColumnFilterType.Date},
        {code: 'NumFacture', label: 'N° de facture', sortable: true},
        {code: 'LibelleFacture', label: 'Libellé', sortable: true},
        {code: 'MontantFacture', label: 'Montant', sortable: true, columnFilterType: ColumnFilterType.Number},
        {code: 'Solde', label: 'Reste dû', sortable: true, columnFilterType: ColumnFilterType.Number},
        {code: 'BenefNom', label: 'Bénéficiaire', sortable: true},
        {code: 'TypeFacture', label: 'Type', sortable: true, columnFilterType: null},
    ];

    columnsForCSVExport: KalTableHeader<T>[] = [
        {
            code: 'StatutFacture',
            label: 'Statut',
            exporter: (row, code) => this.getStatusClass(row[code as string])?.label
        },
        {code: 'DateFacture', label: 'Date', exporter: exportDate},
        {code: 'NumFacture', label: 'N° de facture'},
        {code: 'LibelleFacture', label: 'Libellé'},
        {code: 'MontantFacture', label: 'Montant', exporter: exportAmount},
        {code: 'Solde', label: 'Reste dû', exporter: exportAmount},
        {
            code: 'BenefNom', label: 'Bénéficiaire', exporter: (row) => {
                return this.getBenefFullNameWithSerial(row);
            }
        },
        {code: 'RemarquePublique', label: 'Commentaire public'},
        {code: 'RemarquePrivee', label: 'Commentaire privé'}
    ];

    exportName = 'historique-factures-ventes';

    shouldDisplayFilters = false;

    selectedDeliveriesIdsList: T[] = [];

    loading$: Observable<boolean>;

    @Input() beneficiaryId = null;

    @Coerce('boolean')
    @Input()
    isFreeFromBeneficiaryDetail = false;

    @ViewChild(KalTableComponent) table: KalTableComponent<T>;

    @ViewChild(ListingComponent) listingComponent: ListingComponent<T>;

    invoiceTypeControl: FormControl;

    readonly paymentsList = {
        IsFacture: TypeReglement.Beneficiaire,
        IsAvoir: TypeReglement.RemboursementAvoirClient,
        IsAcompte: TypeReglement.AcompteClient,
    };

    constructor(protected readonly cdr: ChangeDetectorRef,
                protected readonly graphqlService: GraphqlService,
                protected readonly dialogService: KalDialogService,
                protected readonly snackbarService: KalSnackbarService,
                protected readonly activityPrintMailService: ActivityPrintMailService,
                protected readonly layoutService: LayoutService,
                protected readonly route: ActivatedRoute) {
        super(cdr, graphqlService, dialogService, snackbarService);
    }

    get freeQueryParam(): string {
        return this.route.snapshot.queryParamMap.get(isFree);
    }

    get isFreeQueryParam(): boolean {
        return !!this.freeQueryParam;
    }

    set loading(loading: boolean) {
        this.dataSource.loading.set(loading);
    }

    get listingId(): string {
        return SharedInvoicesListComponent.listingKey;
    }

    get countFilters(): string {
        const countFilters = this.dataSource && this.dataSource.countFilters() || null;
        return countFilters ? `(${countFilters})` : '';
    }

    get displayedColumns(): string[] {
        const columns = this.columns.map(({code}) => code);

        // Ce composant est également utilisé dans la fiche signalétique
        // Si on a un beneficiayId on cache la colonne BenefNom sinon on cache la colonne TypeFacture
        const columnIndex = columns.findIndex(code => code === (this.beneficiaryId ? 'BenefNom' : 'TypeFacture'));
        columns.splice(columnIndex, 1);

        return columns as string[];
    }

    get hasVersion(): boolean {
        return this.snapshot(UserState.hasVersion)?.(2.1) || false;
    }

    virtualRowClassFunction = (invoice: T): string => invoice.IsSupprime ? 'deleted' : '';

    canNavigateToInvoice(invoice: T): boolean {
        return invoice.IsFacture && !invoice.IsSupprime && this.hasVersion;
    }

    canNavigateToOrderDetail(invoice: T): boolean {
        return !invoice.IsAvoir && !invoice.IsSupprime && this.hasVersion;
    }

    getBenefFullNameWithSerial(invoice: T): string {
        const {BenefNom, BenefPrenom, BenefMat} = invoice;
        return BenefNom + ' ' + BenefPrenom + (BenefMat ? ' (' + BenefMat + ')' : '');
    }

    getStatusClass(status: string): { label: string, class: string } {
        switch (status) {
            case constants.facture.statut.SOLDE:
                return {label: 'Soldée', class: 'gup-U-color--green'};
            case constants.facture.statut.PARTIELLEMENT_SOLDE:
                return {label: 'Partiellement soldée', class: 'gup-U-color--orange'};
            case constants.facture.statut.NON_SOLDE:
                return {label: 'Non Soldée', class: 'gup-U-color--red'};
        }
    }

    openFilters(): void {
        this.shouldDisplayFilters = !this.shouldDisplayFilters;
    }

    openPrintDialog(row: T): void {
        const invoiceType = this.getInvoiceTypeLabel(row);
        const invoiceTypeLabel = invoiceType[0].toUpperCase() + invoiceType.slice(1);

        const config = new KalDialogConfig<SharedPrintOptionsDialogData>({
            title: 'Impressions',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            disableClose: true,
            data: {
                printOptions: [
                    {code: 'invoice', label: invoiceTypeLabel},
                    {code: 'receipt', label: 'Ticket de caisse'}
                ]
            }
        });

        this.watch$ = this.dialogService.open(SharedPrintOptionsDialogComponent, config).afterClosed.pipe(
            filter(e => !!e && e.accepted),
            map(e => {
                const getProperty = (property: string): boolean => e.printOptions.find(printOption => printOption.code === property).selected;

                return this.activityPrintMailService.getBeneficiaryViewerDocumentsListByPrintInfos(
                    {
                        invoiceId: row.IdFacture,
                        invoiceNumber: row.NumFacture,
                        invoiceLabel: invoiceTypeLabel,
                        activityType: this.route.snapshot.params.activityType
                    },
                    {
                        invoice: getProperty('invoice'),
                        receipt: getProperty('receipt')
                    });
            }),
            filter(viewerDocumentsList => viewerDocumentsList && viewerDocumentsList.length > 0),
            switchMap(viewerDocumentsList => this.openViewerDocumentDialog(viewerDocumentsList))
        );
    }

    openCommentDialog(invoice: T): void {
        this.watch$ = super.openInvoiceCommentDialog(invoice);
    }

    sendMail(invoice: T): void {
        const config = new KalDialogConfig({
            title: 'Envoyer un e-Mail',
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                mailParameters: [{
                    idsList: [invoice.IdFacture],
                    publipostageCode: 'FAC',
                    documentCodesList: ['FACVTE']
                }]
            }
        });

        this.dialogService.open(SharedMailDialogComponent, config);
    }

    openChequeLetterDialog(invoice: T): void {
        this.watch$ = this.openChequeLetterDialog$(invoice);
    }

    private openChequeLetterDialog$(invoice: T): Observable<SharedChequeLetterDialogData|null> {
        this.dataSource.loading.set(true);

        return this.isInvoiceChequeLetterPrintable$(invoice.IdFacture).pipe(
            tap(() => this.dataSource.loading.set(false)),
            switchMap(isInvoiceChequeLetterPrintable => {
                if(!isInvoiceChequeLetterPrintable) {
                    return of(null);
                }

                const isToSupplier = invoice.TypeFacture !== 'C';
                const dialogConfig = new KalDialogConfig<SharedChequeLetterDialogData>({
                    hasBackdrop: true,
                    backdropClass: 'cdk-overlay-dark-backdrop',
                    data: {
                        isToSupplier,
                        context: isToSupplier ? TypeReglement.Fournisseur : TypeReglement.AcompteClient,
                        checkDepositsId: [invoice.IdEcriture]
                    }
                });

                return this.dialogService.open(SharedChequeLetterDialogComponent, dialogConfig).afterClosed;
            }),
            tap(dialogResult => {
                if(!dialogResult?.accepted) {
                    return;
                }

                const config: KalDialogConfig<ViewerDocumentDialogData> = {
                    title: 'Imprimer la lettre chèque',
                    hasBackdrop: true,
                    backdropClass: 'cdk-overlay-dark-backdrop',
                    data: {
                        viewerDocumentsList: [dialogResult.document]
                    }
                };

                this.dialogService.open(ViewerDocumentDialogComponent, config);
            }),
            catchError(err => {
                this.dataSource.loading.set(false);
                return throwError(err);
            })
        );
    }

    navigateToOrderDetail(invoice: T): void {
        this.watch$ = this.getOrderIdFromInvoice(invoice).pipe(
            filter(e => !!e),
            switchMap(orderId => {
                let basePath = '';
                let orderIdPath = '';
                let queryParams: any;

                if (invoice.TypeFacture === 'F') {
                    basePath = 'suppliers';
                    orderIdPath = `${orderId}`;
                } else {
                    queryParams = {orderId};
                }

                return this.layoutService.navigate({
                    content: [basePath, 'orders', 'detail', orderIdPath],
                    focus: 'empty'
                }, {
                    primaryRoute: 'content',
                    activityTypeFrom: invoice.CodeTypeActivite as TypeActivite,
                    queryParams: {
                        ...queryParams,
                        isFree: this.freeQueryParam,
                    },
                });
            })
        );
    }

    stopPropagation($event: MouseEvent): void {
        $event.stopPropagation();
    }

    navigateToInvoiceDetail(invoice: T): void {
        if (invoice.IsFacture && !invoice.IsSupprime) {

            this.layoutService.navigate({
                content: ['invoices', 'detail', `${invoice.IdFacture}`],
                focus: 'empty'
            }, {
                primaryRoute: 'content',
                activityTypeFrom: invoice.CodeTypeActivite as TypeActivite,
                queryParams: {
                    isFree: this.freeQueryParam
                },
            });
        }
    }

    navigateToBeneficiaryDetail(beneficiaryId: number): void {
        this.layoutService.navigate({
            content: ['beneficiary', 'detail'],
            focus: ['beneficiary', 'detail', 'activities']
        }, {
            primaryRoute: 'content',
            queryParams: {[constants.queryParamNames.beneficiaryIdQueryParamKey]: beneficiaryId}
        });
    }

    navigateToFreeEntryInvoiceCreate(): void {
        if(!this.isFreeQueryParam) {
            return;
        }

        this.layoutService.navigate({
            content: ['free-entries', 'beneficiaries', 'invoices', 'create'],
            focus: 'empty'
        }, {
            primaryRoute: 'content',
            activityTypeFrom: this.route
        });
    }

    openCreditDialog(invoice: T): void {
        let dialogConfig = null;

        this.watch$ = this.getRemainingAmount(invoice).pipe(
            switchMap(remainingAmount => {
                if (remainingAmount === 0) {
                    dialogConfig = new KalDialogConfig({
                        title: 'Création d\'un avoir',
                        disableClose: true,
                        hasBackdrop: true,
                        backdropClass: 'cdk-overlay-dark-backdrop',
                        data: {
                            body: 'Impossible de créer un avoir sur une facture totalement avoirisée.',
                            cancelLabel: 'Fermer'
                        }
                    });

                    return this.dialogService.open(ConfirmDialogComponent, dialogConfig).afterClosed.pipe(switchMap(() => EMPTY));
                } else {
                    dialogConfig = new KalDialogConfig<SharedGenerateCreditDialogData>({
                        disableClose: true,
                        hasBackdrop: true,
                        backdropClass: 'cdk-overlay-dark-backdrop',
                        data: {
                            invoice,
                            amount: remainingAmount,
                            activityId: this.snapshot(ActivitySalesState.currentActivityId) || invoice?.IdActivite || 0
                        }
                    });

                    return this.dialogService.open(SharedGenerateCreditDialogComponent, dialogConfig).afterClosed;
                }
            }),
            filter((e: SharedGenerateCreditDialogData) => e?.accepted),
            tap(() => {
                this.snackbarService.open({
                    title: 'L\'avoir a bien été créé.'
                });
                this.dataSource.refresh({forceRefresh: true});
            })
        );
    }

    openAdvancedPaymentDialog(invoice: T): void {
        const paymentType: TypeReglement = this.paymentsList[Object.keys(this.paymentsList).find(e => invoice[e])];

        this.watch$ = this.getInvoiceAutorization(invoice).pipe(
            switchMap(() => {
                const config = new KalDialogConfig<SharedAdvancedSettlementDialogData>({
                    title: invoice.IsFacture ? 'Régler' : 'Rembourser',
                    disableClose: true,
                    hasBackdrop: true,
                    backdropClass: 'cdk-overlay-dark-backdrop',
                    data: {
                        paymentType,
                        invoiceId: invoice.IdFacture,
                        activityId: invoice.IdActivite,
                        thirdId: invoice.IdBenef
                    }
                });

                return this.dialogService.open(SharedAdvancedSettlementDialogComponent, config).afterClosed;
            }),
            filter(e => !!e?.accepted),
            switchMap(() => {
                this.snackbarService.open({
                    title: invoice.IsFacture ? 'La facture a bien été réglée.' : 'Le remboursement a bien été effectué.'
                });

                return this.openChequeLetterDialog$(invoice);
            }),
            tap(() => this.dataSource.refresh({forceRefresh: true})),
            catchError(err => {
                this.dataSource.loading.set(false);
                return throwError(err);
            })
        );
    }

    protected isInvoiceChequeLetterPrintable$(invoiceId: number): Observable<boolean> {
        return this.graphqlService.mutateOne<'IsLettreChequeImprimablePourFacture', MutationIsLettreChequeImprimablePourFactureArgs>({
            mutation: gql`
                mutation IsLettreChequeImprimablePourFacture(
                    $contexte: ContextInfosInput
                    $idFacture: Float
                ) {
                    IsLettreChequeImprimablePourFacture(
                        contexte: $contexte
                        idFacture: $idFacture
                    )
                }
            `,
            variables: {
                idFacture: invoiceId
            }
        });
    }

    delete(invoice: T): void {
        this.watch$ = this.isDeletable(invoice).pipe(
            switchMap(() => {
                if (invoice.IsAvoir) {
                    return this.deleteCredit(invoice);
                } else {
                    return this.confirmDeleteInvoiceDialog(invoice);
                }
            }),
        );
    }

    getLabel(row: T): string {
        const invoiceTypeLabel = this.getInvoiceTypeLabel(row);
        return (row.IsFacture)
            ? `la ${invoiceTypeLabel}`
            : `l\'${invoiceTypeLabel}`;
    }

    getInvoiceTypeLabel({IsFacture, IsAvoir}: T): string {
        if (IsFacture) {
            return 'facture';
        }

        if (IsAvoir) {
            return 'avoir';
        }

        return 'acompte';
    }

    protected deleteCredit(invoice: T): Observable<any> {
        const config = new KalDialogConfig<SharedDeleteCreditDialogData>({
            title: 'Suppression de l\'avoir',
            data: {
                invoice
            },
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
        });

        return this.dialogService.open(SharedDeleteCreditDialogComponent, config).afterClosed.pipe(
            filter(e => !!e?.accepted),
            switchMap(creditInformations => {
                const amount = invoice.MontantFacture - invoice.Solde;

                if (amount === 0) {
                    this.dataSource.refresh({forceRefresh: true});
                    return EMPTY;
                }
                const configConfirm = new KalDialogConfig<ConfirmDialogData>(config);
                configConfirm.data = {
                    paragraphs: [`Le bénéficiaire ${invoice.BenefNom} ${invoice.BenefPrenom} ${invoice.BenefMat ? '(' + invoice.BenefMat + ')' : ''} possède une ou plusieurs`,
                        `factures d'un montant total de ${amount}€.`,
                        `Voulez-vous effectuer l'encaissement ?`],
                    submitLabel: 'Valider',
                    cancelLabel: 'Annuler'
                };

                return this.dialogService.open(ConfirmDialogComponent, configConfirm).afterClosed.pipe(
                    filter(e => !!e?.accepted),
                    map(() => {
                        const formattedInvoice = cloneDeep(invoice);
                        formattedInvoice.IdFacture = creditInformations.deletingInvoiceId;
                        return formattedInvoice;
                    }),
                    tap(e => this.openAdvancedPaymentDialog(e))
                );
            }),
        );
    }

    protected confirmDeleteInvoiceDialog(invoice: T): Observable<string> {

        const typeLabel = this.getInvoiceTypeLabel(invoice);
        const config: KalDialogConfig<ConfirmDialogData> = {
            title: `Suppression d'${invoice.IsAcompte ? `un` : `une`} ${typeLabel}`,
            data: {
                body: `Souhaitez-vous supprimer ${
                    invoice.IsAcompte
                        ? `l'${typeLabel} lié à la commande n°${invoice.ConcerneCommandeId}`
                        : `la ${typeLabel} n°${invoice.NumFacture}`
                }?`,
                submitLabel: 'Supprimer',
                cancelLabel: 'Annuler'
            },
            disableClose: true,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
        };

        return this.dialogService.open(ConfirmDialogComponent, config).afterClosed
            .pipe(
                filter(data => !!data?.accepted),
                switchMap(() => this.deleteInvoice(invoice))
            );
    }

    protected deleteInvoice(invoice: T): Observable<any> {
        this.dataSource.loading.set(true);

        const invoiceDeletionCall = invoice.TypeFacture === 'C'
            ? this.deleteBeneficiaryInvoice(invoice)
            : this.deleteSupplierInvoice(invoice);

        return invoiceDeletionCall.pipe(
            tap((errorMessage) => {
                this.dataSource.loading.set(false);
                let title: string;
                if (!!errorMessage) {
                    title = errorMessage;
                } else {
                    if (invoice.IsAcompte) {
                        title = `L'acompte concernant la commande n°${invoice.ConcerneCommandeId} a été supprimé`;
                    } else if (invoice.IsFacture) {
                        title = `La facture n°${invoice.NumFacture} a été supprimée`;
                    }
                    this.dataSource.refresh({forceRefresh: true});
                }
                this.snackbarService.open({title});
            }),
            catchError(err => {
                this.dataSource.loading.set(false);
                return throwError(err);
            })
        );
    }

    protected deleteBeneficiaryInvoice(invoice: T): Observable<any> {
        return this.graphqlService.mutateOne<'SupprimerFactureReservation', MutationSupprimerFactureReservationArgs>({
            mutation: gql`
                mutation SupprimerFactureReservation(
                    $contexte: ContextInfosInput,
                    $IdFacture: Float
                ) {
                    SupprimerFactureReservation(
                        contexte: $contexte
                        IdFacture: $IdFacture
                    )
                }
            `,
            variables: {
                IdFacture: invoice.IdFacture,
            },
        });
    }

    protected deleteSupplierInvoice(invoice: T): Observable<any> {
        return this.graphqlService.mutateOne<'SupprimerFactureFournisseur', MutationSupprimerFactureFournisseurArgs>({
            mutation: gql`
                mutation SupprimerFactureFournisseur(
                    $contexte: ContextInfosInput,
                    $IdFac: Float
                ){
                    SupprimerFactureFournisseur(
                        contexte: $contexte
                        IdFac: $IdFac
                    )
                }
                    `,
            variables: {
                IdFac: invoice.IdFacture
            }
        });
    }

    protected isDeletable(invoice: T): Observable<any> {
        const dialogConfig = new KalDialogConfig({
            title: `Suppression de ${this.getLabel(invoice)}`,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                body: '',
                cancelLabel: 'Fermer'
            }
        });

        if (invoice.IsAvoir && invoice.TypeFacture === 'C') {
            if (invoice.MontantFacture === 0) {
                dialogConfig.data.body = `L'avoir n°${invoice.NumFacture} d'un montant de 0€ ne peut pas être supprimé`;
            } else if (invoice.NumeroFactureAnnulationAvoirAssociee) {
                dialogConfig.data.body = `
                        L'avoir n°${invoice.NumFacture} ne peut pas être supprimé.
                        Il existe déjà une facture d'annulation pour cet avoir : ${invoice.NumeroFactureAnnulationAvoirAssociee}
                    `;
            }
        }

        if (dialogConfig.data.body) {
            return this.dialogService.open(ConfirmDialogComponent, dialogConfig).afterClosed.pipe(switchMap(() => EMPTY));
        }

        this.dataSource.loading.set(true);

        return this.graphqlService.mutateOne<'IsSupprimableFacture', MutationIsSupprimableFactureArgs>({
            mutation: gql`
                mutation IsSupprimableFacture(
                    $contexte: ContextInfosInput,
                    $IdFac: Float
                ) {
                    IsSupprimableFacture(
                        contexte: $contexte,
                        IdFac: $IdFac
                    )
                }
            `,
            variables: {
                IdFac: invoice.IdFacture
            },
            context: {
                [IGNORE_ALL_GRAPHQL_ERRORS]: true
            }
        }).pipe(
            tap(() => this.dataSource.loading.set(false)),
            catchError(err => {
                this.dataSource.loading.set(false);
                dialogConfig.data.body = graphqlErrors(err);
                return this.dialogService.open(ConfirmDialogComponent, dialogConfig).afterClosed.pipe(switchMap(() => EMPTY));
            })
        );
    }

    protected getRemainingAmount(invoice: T): Observable<number> {
        return this.graphqlService.mutateOne<'VerifMontantRestantAvoirSurFacture', MutationVerifMontantRestantAvoirSurFactureArgs>({
            mutation: gql`
                mutation VerifMontantRestantAvoirSurFacture(
                    $contexte: ContextInfosInput,
                    $IdFacture: Float
                ) {
                    VerifMontantRestantAvoirSurFacture(
                        contexte: $contexte
                        IdFacture: $IdFacture
                    ) {
                        DifferenceFactureAvoir
                    }
                }
            `,
            variables: {
                IdFacture: invoice.IdFacture
            }
        }).pipe(
            map(VerifMontantRestantAvoirSurFacture => VerifMontantRestantAvoirSurFacture?.DifferenceFactureAvoir || 0)
        );
    }

    protected getOrderIdFromInvoice(invoice: T): Observable<any> {
        const config = new KalDialogConfig({
            title: 'Sélection d\'une commande',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
        });

        if (invoice.IsAcompte) {
            return of(invoice.ConcerneCommandeId);
        } else if (!invoice.IsFacture) {
            return of(null);
        }

        this.loading = true;

        return this.graphqlService.mutateOne<'GetListeCommandeHistorique', MutationGetListeCommandeHistoriqueArgs>({
            mutation: gql`
                mutation GetListeCommandeHistorique(
                    $contexte: ContextInfosInput,
                    $criteres: CriteresDevisCommandeModelInput
                ) {
                    GetListeCommandeHistorique(
                        contexte: $contexte
                        criteres: $criteres
                    ) {
                        Count
                        ListeElements {
                            DevisCommandeListingModel {
                                IdCommande
                                DateCommande
                                NomFournisseur
                                LibelleCommande
                                MontantTotalCommande
                            }
                        }
                    }
                }
            `,
            variables: {
                criteres: {
                    IdFacture: invoice.IdFacture
                }
            }
        }).pipe(
            map(GetListeCommandeHistorique => GetListeCommandeHistorique?.ListeElements?.DevisCommandeListingModel || []),
            switchMap(ordersList => {
                this.loading = false;

                if (ordersList.length > 1) {

                    config.data = {
                        ordersList
                    };

                    return this.dialogService.open(SharedInvoicesOrdersListDialogComponent, config).afterClosed.pipe(
                        filter(e => !!e?.accepted),
                        map(e => e.selectedOrderId)
                    );
                } else {
                    return of(ordersList[0]?.IdCommande || null);
                }
            }),
            switchMap(selectedOrderId => {
                if (!selectedOrderId) {
                    config.data = {
                        cancelLabel: 'Fermer',
                        body: `Il n'y a pas de commande associée à la facture N°${invoice.NumFacture}.`
                    };

                    return this.dialogService.open(ConfirmDialogComponent, config).afterClosed.pipe(
                        switchMap(() => EMPTY)
                    );
                }

                return of(selectedOrderId);
            })
        );
    }

    protected createDatasource(): void {
        this.dataSource = new SharedInvoicesListDatasource({
                loading: true,
                sort: SharedInvoicesListComponent.defaultSort,
                filters: {
                    ...SharedInvoicesFiltersComponent.defaultFilters(this.beneficiaryId)
                }
            },
            this.graphqlService,
            this.store,
            {
                isFree: this.isFreeFromBeneficiaryDetail ? null : this.route.snapshot.queryParamMap.get(isFree) === '1'
            }
        );

        this.loading$ = this.dataSource.loading.change$;

        // when id activity change, update datasource
        this.watch$ = this.activityId$.pipe(
            // Permet d'éviter plusieurs appels pour récupérer la liste des factures
            delay(1),
            distinctUntilChanged(),
            // update datasource
            tap(() => {
                this.dataSource.reset(this.dataSource.parameters, {forceRefresh: true});
            })
        );
    }

    ngOnInit(): void {
        this.createDatasource();

        this.invoiceTypeControl = new FormControl(SharedInvoicesFiltersComponent.defaultFilters(this.beneficiaryId)?.TypesFactures);

        this.watch$ = this.dataSource.filters.change$.pipe(
            tap(filters => {
                this.invoiceTypeControl.patchValue(filters?.TypesFactures, {emitEvent: false});
            })
        );

        // Les filtres rapide sur la fiche signalétique
        this.watch$ = this.invoiceTypeControl.valueChanges.pipe(
            tap(value => {
                const filters = cloneDeep(this.listingComponent.filters);
                filters.TypesFactures = value;

                this.listingComponent.updateForm(filters);
                this.listingComponent.applyFilters(filters);
            }),
        );
    }
}
